import ExecutiveDiagramFrame from './ExecutiveDiagramFrame';

/**
 * Animated follow-the-clock trading illustration — equities, options, global sessions.
 */
const PATiFollowTheClockDiagram = ({ theme = 'dark' }) => {
  const isDark = theme === 'dark';
  const bg = isDark ? '#060d18' : '#f0f9ff';
  const grid = isDark ? '#1e293b' : '#cbd5e1';
  const label = isDark ? '#f1f5f9' : '#0f172a';
  const muted = isDark ? '#64748b' : '#64748b';
  const teal = isDark ? '#2dd4bf' : '#0d9488';
  const sapphire = isDark ? '#60a5fa' : '#2563eb';
  const amber = isDark ? '#fbbf24' : '#d97706';
  const violet = isDark ? '#a78bfa' : '#7c3aed';
  const emerald = isDark ? '#34d399' : '#059669';

  return (
    <ExecutiveDiagramFrame
      theme={theme}
      caption="Follow-the-clock orchestration · equities · options · supervised execution"
      className="pati-ftc-frame"
    >
      <svg
        viewBox="0 0 720 480"
        className="w-full h-auto pati-ftc-svg"
        role="img"
        aria-label="Illustration of PATi follow-the-clock trading across global market sessions with equities and options supervision"
      >
        <defs>
          <linearGradient id="patiCoreGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={teal} stopOpacity="0.9" />
            <stop offset="50%" stopColor={sapphire} stopOpacity="0.85" />
            <stop offset="100%" stopColor={violet} stopOpacity="0.8" />
          </linearGradient>
          <linearGradient id="patiOrbitGrad" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor={teal} stopOpacity="0" />
            <stop offset="40%" stopColor={teal} stopOpacity="0.7" />
            <stop offset="100%" stopColor={sapphire} stopOpacity="0" />
          </linearGradient>
          <linearGradient id="patiEquityGrad" x1="0%" y1="100%" x2="0%" y2="0%">
            <stop offset="0%" stopColor={emerald} stopOpacity="0.2" />
            <stop offset="100%" stopColor={emerald} stopOpacity="0.9" />
          </linearGradient>
          <filter id="patiGlow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="4" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <filter id="patiSoftGlow" x="-30%" y="-30%" width="160%" height="160%">
            <feGaussianBlur stdDeviation="8" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        <rect width="720" height="480" fill={bg} rx="12" />

        {/* Grid */}
        {Array.from({ length: 13 }).map((_, i) => (
          <line
            key={`vg-${i}`}
            x1={60 + i * 50}
            y1="40"
            x2={60 + i * 50}
            y2="440"
            stroke={grid}
            strokeWidth="0.5"
            opacity="0.35"
          />
        ))}
        {Array.from({ length: 9 }).map((_, i) => (
          <line
            key={`hg-${i}`}
            x1="40"
            y1={60 + i * 48}
            x2="680"
            y2={60 + i * 48}
            stroke={grid}
            strokeWidth="0.5"
            opacity="0.35"
          />
        ))}

        <text x="360" y="32" textAnchor="middle" fill={muted} fontSize="10" letterSpacing="4" fontWeight="600">
          GLOBAL SESSION CLOCK
        </text>

        {/* Clock ring */}
        <circle cx="360" cy="248" r="148" fill="none" stroke={grid} strokeWidth="1" opacity="0.5" />
        <circle cx="360" cy="248" r="132" fill="none" stroke={grid} strokeWidth="0.5" strokeDasharray="3 6" opacity="0.4" />

        {/* Session arcs */}
        <path
          className="pati-ftc-arc pati-ftc-arc-us"
          d="M 360 116 A 132 132 0 0 1 474 200"
          fill="none"
          stroke={sapphire}
          strokeWidth="10"
          strokeLinecap="round"
          opacity="0.55"
        />
        <path
          className="pati-ftc-arc pati-ftc-arc-eu"
          d="M 474 296 A 132 132 0 0 1 246 296"
          fill="none"
          stroke={teal}
          strokeWidth="10"
          strokeLinecap="round"
          opacity="0.55"
        />
        <path
          className="pati-ftc-arc pati-ftc-arc-asia"
          d="M 246 200 A 132 132 0 0 1 360 116"
          fill="none"
          stroke={amber}
          strokeWidth="10"
          strokeLinecap="round"
          opacity="0.55"
        />

        {/* Rotating sweep */}
        <g className="pati-ftc-sweep" style={{ transformOrigin: '360px 248px' }}>
          <line x1="360" y1="248" x2="360" y2="124" stroke="url(#patiOrbitGrad)" strokeWidth="2" />
          <circle cx="360" cy="124" r="5" fill={teal} filter="url(#patiGlow)" />
        </g>

        {/* Orbital execution pulse */}
        <circle className="pati-ftc-pulse" cx="360" cy="116" r="6" fill={teal} filter="url(#patiGlow)" />

        {/* Session labels */}
        {[
          { x: 430, y: 148, t: 'US', sub: 'Equities · Options', c: sapphire },
          { x: 430, y: 348, t: 'EUROPE', sub: 'Listings', c: teal },
          { x: 290, y: 348, t: 'ASIA', sub: 'Sessions', c: amber },
          { x: 248, y: 168, t: '24/7', sub: 'Crypto', c: violet },
        ].map(({ x, y, t, sub, c }) => (
          <g key={t}>
            <rect x={x - 38} y={y - 14} width="76" height="36" rx="6" fill={isDark ? '#0f172a' : '#fff'} opacity="0.85" />
            <text x={x} y={y + 2} textAnchor="middle" fill={c} fontSize="11" fontWeight="700" letterSpacing="1">
              {t}
            </text>
            <text x={x} y={y + 16} textAnchor="middle" fill={muted} fontSize="8">
              {sub}
            </text>
          </g>
        ))}

        {/* Supervision envelope — hands-off but governed */}
        <ellipse
          className="pati-ftc-supervision"
          cx="360"
          cy="248"
          rx="168"
          ry="168"
          fill="none"
          stroke={emerald}
          strokeWidth="1"
          strokeDasharray="8 12"
          opacity="0.45"
        />

        {/* Center core */}
        <circle cx="360" cy="248" r="52" fill="none" stroke="url(#patiCoreGrad)" strokeWidth="2" className="pati-ftc-core-ring" />
        <circle cx="360" cy="248" r="38" fill={isDark ? '#0c1424' : '#e0f2fe'} stroke={teal} strokeWidth="1.5" opacity="0.9" />
        <text x="360" y="244" textAnchor="middle" fill={label} fontSize="14" fontWeight="800" letterSpacing="1">
          PATi
        </text>
        <text x="360" y="262" textAnchor="middle" fill={muted} fontSize="8" letterSpacing="2">
          OPERATING CORE
        </text>

        {/* Equities panel — left */}
        <g>
          <rect x="52" y="88" width="118" height="130" rx="10" fill={isDark ? '#0f172a' : '#fff'} stroke={grid} strokeWidth="1" opacity="0.9" />
          <text x="111" y="108" textAnchor="middle" fill={emerald} fontSize="9" fontWeight="700" letterSpacing="2">
            EQUITIES
          </text>
          {/* Candlesticks */}
          {[
            { x: 72, h: 42, bull: true },
            { x: 92, h: 28, bull: false },
            { x: 112, h: 55, bull: true },
            { x: 132, h: 36, bull: true },
            { x: 152, h: 48, bull: false },
          ].map(({ x, h, bull }, i) => (
            <g key={x} className={`pati-ftc-candle pati-ftc-candle-${i}`}>
              <line x1={x} y1={200 - h} x2={x} y2={188} stroke={bull ? emerald : '#f87171'} strokeWidth="1.5" />
              <rect
                x={x - 6}
                y={200 - h}
                width="12"
                height={h * 0.65}
                fill={bull ? 'url(#patiEquityGrad)' : '#f87171'}
                opacity={bull ? 0.85 : 0.55}
                rx="1"
              />
            </g>
          ))}
          <polyline
            className="pati-ftc-price-line"
            points="68,195 88,178 108,186 128,160 148,168 168,148"
            fill="none"
            stroke={sapphire}
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <text x="111" y="208" textAnchor="middle" fill={muted} fontSize="8">
            Intraday · Scoped
          </text>
        </g>

        {/* Options panel — right */}
        <g>
          <rect x="550" y="88" width="118" height="130" rx="10" fill={isDark ? '#0f172a' : '#fff'} stroke={grid} strokeWidth="1" opacity="0.9" />
          <text x="609" y="108" textAnchor="middle" fill={violet} fontSize="9" fontWeight="700" letterSpacing="2">
            OPTIONS
          </text>
          {[0, 1, 2, 3].map((i) => (
            <g key={i} className={`pati-ftc-strike pati-ftc-strike-${i}`}>
              <line x1="568" y1={128 + i * 22} x2="650" y2={128 + i * 22} stroke={grid} strokeWidth="0.5" />
              <circle cx="580" cy={128 + i * 22} r="4" fill={violet} opacity="0.7" />
              <circle cx="609" cy={128 + i * 22} r="5" fill={i === 1 ? teal : violet} opacity={i === 1 ? 1 : 0.45} filter={i === 1 ? 'url(#patiGlow)' : undefined} />
              <circle cx="638" cy={128 + i * 22} r="4" fill={amber} opacity="0.55" />
            </g>
          ))}
          <text x="609" y="208" textAnchor="middle" fill={muted} fontSize="8">
            Chain · Risk-bounded
          </text>
        </g>

        {/* Flow rail — bottom */}
        <rect x="80" y="400" width="560" height="52" rx="10" fill={isDark ? '#0f172a' : '#fff'} stroke={grid} strokeWidth="1" opacity="0.85" />
        {[
          { x: 130, label: 'Premarket' },
          { x: 250, label: 'Execute' },
          { x: 370, label: 'Supervise' },
          { x: 490, label: 'Reconcile' },
          { x: 590, label: 'Review' },
        ].map(({ x, label: lbl }, i) => (
          <g key={lbl}>
            <circle cx={x} cy="426" r="14" fill="none" stroke={i <= 2 ? teal : grid} strokeWidth="1.5" className={`pati-ftc-phase pati-ftc-phase-${i}`} />
            <text x={x} y="430" textAnchor="middle" fill={i <= 2 ? label : muted} fontSize="8" fontWeight="600">
              {i + 1}
            </text>
            <text x={x} y="448" textAnchor="middle" fill={muted} fontSize="7">
              {lbl}
            </text>
            {i < 4 && (
              <line x1={x + 18} y1="426" x2={x + 52} y2="426" stroke={grid} strokeWidth="1" strokeDasharray="4 3" />
            )}
          </g>
        ))}

        {/* Hands-off indicator */}
        <g className="pati-ftc-hands-off">
          <rect x="296" y="318" width="128" height="28" rx="14" fill={isDark ? '#0f172a' : '#ecfdf5'} stroke={emerald} strokeWidth="1" opacity="0.9" />
          <text x="360" y="336" textAnchor="middle" fill={emerald} fontSize="9" fontWeight="700" letterSpacing="2">
            HANDS-OFF · GOVERNED
          </text>
        </g>
      </svg>
    </ExecutiveDiagramFrame>
  );
};

export default PATiFollowTheClockDiagram;
